import { first, isEmpty, memoize, size, trim } from 'lodash'
import { saveAs } from 'file-saver'
import { getInstances } from 'api/instances'
import {
  put,
  getAll,
  remove,
  insert,
  getFiles,
  getFromDocument,
  downloadFileById,
  downloadFileByPathname,
  removeMultiple
} from 'api/scenarios'
import {
  deleteMultipleScheduled,
  deleteScheduled,
  getScheduledScenarios,
  insertScheduled,
  editScheduled,
  simulateScheduled
} from 'services/api/scheduled'

import {
  deletePurger,
  deleteMultiplePurger,
  getPurgerScenarios,
  insertPurger
} from 'services/api/purger'

import { openModal } from 'components/Modal/actions'

import { updateBuffer } from 'pages/Scenario/utils/state'
import { RESET, WAIT, DELETING, CANCELLING, KILLING } from './constants'
import { sendNotification } from './Manager/notifications'

import mock from './Purger/mock.json'
/* ------------------------- Instances and Document section ------------------------- */

export const getInstancesList = async () => {
  return await getInstances()
}

// used from scenario and wizard both
export const fetchDataFromDocument = async ({ params, instanceId }) => {
  return await getFromDocument({ params, instanceId })
}

/* ------------------------- Scenario section ------------------------- */

export const getScenarioList = async () => {
  const {
    body: { items }
  } = await getAll()

  return items
}

export const getScenarioListByIds = async (idList, t) => {
  try {
    const params = new URLSearchParams()

    for (const id of idList) {
      params.append('id', id)
    }

    const {
      body: { items }
    } = await getAll(params)

    for (const item of items) {
      item.typeValue = item.type?.value

      if (item.status === 'CREATED') {
        sendNotification({
          t,
          kind: 'success',
          action: 'create',
          name: item.name,
          username: item.username
        })
      }
    }

    return { items }
  } catch (error) {
    console.error(error)
  }
}

export const createScenario = async (data) => {
  return await insert(data)
}

export const removeScenario = async (scenario) => {
  const { id, status } = scenario

  try {
    updateBuffer({
      [id]: {
        status: DELETING
      }
    })
    await remove(id)
  } catch (err) {
    updateBuffer({
      [id]: {
        status
      }
    })
  }
}

export const removeScenarios = async (scenarios, ids) => {
  try {
    ids.forEach((id) => {
      updateBuffer({
        [id]: {
          status: DELETING
        }
      })
    })

    await removeMultiple(ids)
  } catch (err) {
    scenarios.forEach(({ id, status }) => {
      updateBuffer({
        [id]: {
          status
        }
      })
    })
  }
}

export const runScenario = async (scenario) => {
  const { id } = scenario

  updateBuffer({
    [id]: {
      status: WAIT
    }
  })

  const body = {
    data: {},
    scenarioManager: { status: WAIT }
  }

  await put(id, body)
}

export const resetScenario = async ({ data }) => {
  const { id, logDir } = data

  const body = {
    data: {},
    scenarioManager: { status: RESET }
  }

  const onUpdate = () => put(id, body)
  const onDownloadFile = () => downloadError(data)

  try {
    // Check if a log file is present
    // If there is, show a modal to ask if they want to download the file
    if (logDir) {
      openModal('scenarioReset', { onDownloadFile, onUpdate })
    } else {
      await put(id, body)
    }
  } catch (error) {
    console.log('error on reset')
  }
}

export const cancelScenario = async ({ scenario }) => {
  const { id } = scenario

  updateBuffer({
    [id]: {
      status: CANCELLING
    }
  })

  const body = {
    data: {},
    scenarioManager: { status: CANCELLING }
  }

  await put(id, body)
}

export const killScenario = async ({ scenario }) => {
  const { id } = scenario

  updateBuffer({
    [id]: {
      status: CANCELLING
    }
  })

  const body = {
    data: {},
    scenarioManager: { status: KILLING }
  }

  await put(id, body)
}

/* ------------------------- File section ------------------------- */

export const downloadFile = async ({ id, name }) => {
  const { body } = await downloadFileById(id)

  saveAs(body, name)
}

export const getFileInfo = async (id) => {
  const files = await getFiles()

  return files.find((file) => file.id === id)
}

export const getFileByScenarioId = async ({
  id: scenarioEventId,
  activityId: scenarioActivityId
}) => {
  const { body: files } = await getFiles()

  return files.find(
    ({ name, eventId, activityId }) =>
      eventId === scenarioEventId &&
      activityId === scenarioActivityId &&
      name.endsWith('Error.txt')
  )
}

const processFileName = (name) => trim(name, '"')

export const downloadError = async ({ name, logDir }) => {
  if (logDir) {
    const res = await downloadFileByPathname(logDir)
    const { body, fileName } = res
    if (body === '')
      return console.error('Error: No content - Blob is required')

    saveAs(body, fileName ? processFileName(fileName) : `${name}_Error.txt`)
  }
}

/* ------------------------- Scheduled section ------------------------- */

export const getScheduledScenarioList = async () => {
  try {
    const res = await getScheduledScenarios()
    if (res) {
      const { body: items } = res
      return items
    }
  } catch (error) {
    console.log(error)
  }
}

export const createScheduledScenario = async (data) => {
  return await insertScheduled(data)
}

export const editScheduledScenario = async (data, id) => {
  return await editScheduled(data, id)
}

export const simulateScheduledScenario = async ({ payload, id }) => {
  return await simulateScheduled(payload, id)
}

export const removeScheduledScenario = async (scenario) => {
  const { id } = scenario
  return await deleteScheduled({ id })
}

export const removeScheduledScenarios = async (scenarios) => {
  const { ids } = scenarios
  return await deleteMultipleScheduled({ ids })
}

/* ------------------------- Purger section ------------------------- */

export const createPurgerScenario = async (data) => {
  return await insertPurger(data)
}

export const getPurgerScenarioList = async () => {
  try {
    const { body } = await getPurgerScenarios()
    if (body) {
      return body
    }
  } catch (error) {
    console.log(error)
  }
}

export const removePurgerScenario = async (scenario) => {
  const { id } = scenario
  return await deletePurger({ id })
}

export const removePurgerScenarios = async (scenarios) => {
  const { ids } = scenarios
  return await deleteMultiplePurger({ ids })
}
